import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useGallery } from '../context/GalleryContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHeart, FaSignOutAlt, FaExternalLinkAlt, FaTrash, FaLayerGroup } from 'react-icons/fa';
import './Dashboard.css';

const Dashboard = () => {
    const { currentUser, logout } = useAuth();
    const { favorites, toggleFavorite } = useGallery();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    return (
        <div className="dashboard-container">
            <div className="dashboard-hero">
                <div className="hero-overlay"></div>
                <div className="hero-content">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="profile-section"
                    >
                        <div className="profile-avatar">
                            {currentUser?.photoURL ? (
                                <img src={currentUser.photoURL} alt="Profile" />
                            ) : (
                                <div className="avatar-placeholder">
                                    {currentUser?.displayName?.[0] || currentUser?.email?.[0]?.toUpperCase()}
                                </div>
                            )}
                        </div>
                        <div className="profile-info">
                            <h1>{currentUser?.displayName || currentUser?.email?.split('@')[0]}</h1>
                            <p>Personal Collection</p>
                        </div>
                    </motion.div>

                    <div className="dashboard-actions">
                        <div className="stats-badge">
                            <FaHeart />
                            <span>{favorites.length} Favorites</span>
                        </div>
                        <button onClick={handleLogout} className="logout-btn">
                            <FaSignOutAlt /> Logout
                        </button>
                    </div>
                </div>
            </div>

            <div className="dashboard-content">
                {favorites.length === 0 ? (
                    <div className="empty-state-elegant">
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: 0.2 }}
                            className="empty-icon-wrapper"
                        >
                            <FaLayerGroup />
                        </motion.div>
                        <h3>Start Your Collection</h3>
                        <p>Your dashboard is looking a bit empty. Explore the gallery to add photos you love.</p>
                        <button onClick={() => navigate('/')} className="explore-btn-large">
                            Explore Gallery
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="section-header">
                            <h2><FaHeart className="header-icon" /> My Favorites</h2>
                            <div className="divider"></div>
                        </div>

                        <div className="masonry-grid">
                            <AnimatePresence>
                                {favorites.map((photo, index) => (
                                    <motion.div
                                        key={photo.id}
                                        className="masonry-item"
                                        layout
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ delay: index * 0.05 }}
                                    >
                                        <div className="photo-card-premium">
                                            <img
                                                src={photo.urls.regular}
                                                alt={photo.alt_description || "Favorite"}
                                                loading="lazy"
                                                onClick={() => navigate(`/photo/${photo.id}`)}
                                            />
                                            <div className="card-gradient-overlay">
                                                <div className="card-top-actions">
                                                    <button
                                                        className="action-icon delete"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleFavorite(photo);
                                                        }}
                                                        title="Remove"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                                <div className="card-bottom-info">
                                                    <span className="photographer">{photo.user.name}</span>
                                                    <button
                                                        className="view-btn"
                                                        onClick={() => navigate(`/photo/${photo.id}`)}
                                                    >
                                                        View <FaExternalLinkAlt />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
